import { ActionIcon, Flex, Text } from '@mantine/core';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn.tsx';
import {
	createSingleLineHeader,
	SINGLE_LINE_STYLE,
} from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/singleLine.ts';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting.ts';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types.ts';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import { makeComplexDataTestId, TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { InvestorDocumentRow } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/DocumentsCard/investorDocuments.types.ts';
import { formatLinkedToNames } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/DocumentsCard/investorDocuments.utils.ts';
import { LinkedToNames } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/DocumentsCard/LinkedToNames.tsx';

interface Props {
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>;
	onPreview: (row: InvestorDocumentRow) => void;
}

export const getInvestorDocumentsTableColumns = ({
	tMipManage,
	onPreview,
}: Props): ExtendedColumnDef<InvestorDocumentRow>[] => [
	createTextColumn<InvestorDocumentRow>({
		id: 'name',
		size: 220,
		minSize: 160,
		accessorKey: 'name',
		header: createSingleLineHeader<InvestorDocumentRow>(
			tMipManage('investors.details.documents.columns.name')
		),
		columnLabel: tMipManage('investors.details.documents.columns.name'),
		cell: ({ row }) => (
			<Text size="B6Medium" truncate style={SINGLE_LINE_STYLE} title={row.original.name}>
				{row.original.name}
			</Text>
		),
	}),

	createTextColumn<InvestorDocumentRow>({
		id: 'classifications',
		size: 170,
		minSize: 150,
		accessorFn: (row) => row.classifications || LONG_DASH,
		header: createSingleLineHeader<InvestorDocumentRow>(
			tMipManage('investors.details.documents.columns.classifications', {
				defaultValue: 'Classifications',
			})
		),
		columnLabel: tMipManage('investors.details.documents.columns.classifications', {
			defaultValue: 'Classifications',
		}),
	}),

	createTextColumn<InvestorDocumentRow>({
		id: 'linkedTo',
		size: 180,
		minSize: 140,
		accessorFn: (row) => formatLinkedToNames(row.linkedToNames) || LONG_DASH,
		header: createSingleLineHeader<InvestorDocumentRow>(
			tMipManage('investors.details.documents.columns.linked_to')
		),
		columnLabel: tMipManage('investors.details.documents.columns.linked_to'),
		cell: ({ row }) =>
			row.original.linkedToNames.length > 0 ? (
				<LinkedToNames
					names={row.original.linkedToNames}
					textProps={{ size: 'B6Medium' }}
				/>
			) : (
				<Text size="B6Medium">{LONG_DASH}</Text>
			),
	}),

	createTextColumn<InvestorDocumentRow>({
		id: 'linkType',
		size: 120,
		minSize: 120,
		accessorFn: (row) =>
			row.isTransactionDocument
				? tMipManage('investors.details.documents.link_type.transaction')
				: tMipManage('investors.details.documents.link_type.programme'),
		header: createSingleLineHeader<InvestorDocumentRow>(
			tMipManage('investors.details.documents.columns.link_type')
		),
		columnLabel: tMipManage('investors.details.documents.columns.link_type'),
	}),

	createTextColumn<InvestorDocumentRow>({
		id: 'preview',
		size: 50,
		minSize: 50,
		maxSize: 50,
		enableSorting: false,
		enableColumnFilter: false,
		header: () => null,
		columnLabel: tMipManage('investors.details.documents.columns.preview'),
		cell: ({ row }) => (
			<Flex justify="flex-end" w="100%">
				<ActionIcon
					variant="transparent"
					onClick={() => onPreview(row.original)}
					data-testid={makeComplexDataTestId(
						TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_DOCUMENT_PREVIEW,
						row.original.id
					)}
				>
					<Icon variant="eye" size="xl" color="black" />
				</ActionIcon>
			</Flex>
		),
	}),
];
